import { translate } from 'shared/lib/i18n.tsx';
import type { TargetDisplayState } from '../../../domain/TargetDisplayState.ts';
import type { RemoteDisplayState } from '../../../domain/RemoteDisplayState.ts';
import { defaultRemoteViewLayout, type RemoteViewLayout } from 'shared/lib/tv-game/nativeLayout.ts';
import { getRemoteCalibStandZoneMetrics } from 'shared/lib/tv-game/remoteCalibStandZone.ts';
import { drawHudStrip, drawSideStrips } from '../room/letterbox.ts';
import { drawLiveSkeletonPts, drawBladeTrails } from '../room/skeleton.ts';
import { drawCalibBackdrop, drawRemoteCalibrationStandZone } from '../room/calibrationShared.ts';
import { useLighterCanvasEffects, wrapCanvasText } from '../room/canvasHelpers.ts';
import { drawImageCover } from '../room/primitives.ts';

const TARGET_ATTACK_BG = '/media/targetAttackBg.png';

/** Target Attack: фон `targetAttackBg.png`, скелет, мишени, клинки — HUD в React. */
export class TargetAttackCanvasLayer {
  private readonly ctx: CanvasRenderingContext2D;
  private readonly bgImage: HTMLImageElement;
  private bgGradientCache: CanvasGradient | null = null;
  private bgGradientCacheKey = '';

  constructor(private readonly canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Cannot get 2D canvas context');
    this.ctx = ctx;
    // src ставится в preload() — фон грузится только когда выбрана эта игра.
    this.bgImage = new Image();
  }

  preload(): void {
    if (!this.bgImage.src) this.bgImage.src = TARGET_ATTACK_BG;
  }

  render(state: TargetDisplayState): void {
    if (
      state.phase !== 'playing' &&
      state.phase !== 'gameover' &&
      state.phase !== 'calibration'
    ) {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      return;
    }

    const W = this.canvas.width;
    const H = this.canvas.height;
    const ctx = this.ctx;
    ctx.clearRect(0, 0, W, H);
    this.drawTargetAttackBackground(W, H);

    if (state.phase === 'calibration') {
      this.drawTargetCalibrationScreen(state, W, H);
      return;
    }

    const rs = state as unknown as RemoteDisplayState;
    drawHudStrip(this.ctx, rs, W);
    drawSideStrips(this.ctx, rs, H, this.canvas.width);
    const layout = state.remoteView ?? defaultRemoteViewLayout(W, H);
    if (state.splitTwoPlayers) {
      this.drawCenterUiDivider(layout);
    }
    ctx.save();
    ctx.beginPath();
    ctx.rect(layout.offsetX, layout.offsetY, layout.gameWidth, layout.gameHeight);
    ctx.clip();

    const light = useLighterCanvasEffects(this.canvas);

    this.drawTargetCircles(state, layout);
    drawBladeTrails(this.ctx, this.canvas.width, state.bladeTrails, layout, light, state.p1PlayerId, state.p2PlayerId);
    this.drawTargetLabels(state, layout);
    ctx.restore();
  }

  private drawTargetCalibrationScreen(state: TargetDisplayState, W: number, H: number): void {
    const single = !state.splitTwoPlayers || !state.leftView || !state.rightView;
    if (single) {
      const layout = state.remoteView ?? defaultRemoteViewLayout(W, H);
      const ctx = this.ctx;
      ctx.save();
      ctx.beginPath();
      ctx.rect(layout.offsetX, layout.offsetY, layout.gameWidth, layout.gameHeight);
      ctx.clip();
      drawCalibBackdrop(ctx, layout);
      drawLiveSkeletonPts(this.ctx, state.calibNormPoints, layout, false, 56, 255, 180, useLighterCanvasEffects(this.canvas));
      drawRemoteCalibrationStandZone(ctx, layout, 'full');
      this.drawTargetDetectionProgressScan(layout, state.detectionProgress);
      this.drawTargetCalibInstructions(layout);
      ctx.restore();
      return;
    }

    const full = state.remoteView ?? defaultRemoteViewLayout(W, H);
    const lv = state.leftView!;
    const rv = state.rightView!;
    const leftCalibLayout = this.buildWiderSplitCalibLayout(lv, full);
    const rightCalibLayout = this.buildWiderSplitCalibLayout(rv, full);
    const ctx = this.ctx;

    ctx.save();
    ctx.beginPath();
    ctx.rect(full.offsetX, full.offsetY, full.gameWidth, full.gameHeight);
    ctx.clip();
    drawCalibBackdrop(ctx, full);
    drawLiveSkeletonPts(this.ctx, state.calibNormPoints, full, false, 56, 255, 180, useLighterCanvasEffects(this.canvas));
    drawLiveSkeletonPts(this.ctx, state.calibNormPointsP2, full, false, 255, 120, 200, useLighterCanvasEffects(this.canvas));
    this.drawCenterUiDivider(full);
    drawRemoteCalibrationStandZone(ctx, leftCalibLayout, 'full');
    this.drawTargetDetectionProgressScan(leftCalibLayout, state.detectionProgress);
    this.drawTargetCalibInstructions(leftCalibLayout);
    drawRemoteCalibrationStandZone(ctx, rightCalibLayout, 'full');
    this.drawTargetDetectionProgressScan(rightCalibLayout, state.detectionProgress);
    this.drawTargetCalibInstructions(rightCalibLayout);
    ctx.restore();
  }

  private buildWiderSplitCalibLayout(
    splitLayout: RemoteViewLayout,
    fullLayout: RemoteViewLayout,
  ): RemoteViewLayout {
    const desiredWidth = Math.min(fullLayout.gameWidth * 0.58, splitLayout.gameWidth * 1.7);
    const cx = splitLayout.offsetX + splitLayout.gameWidth / 2;
    const offsetX = cx - desiredWidth / 2;
    return {
      ...splitLayout,
      offsetX,
      gameWidth: desiredWidth,
    };
  }

  private drawCenterUiDivider(layout: RemoteViewLayout): void {
    const ctx = this.ctx;
    const midX = layout.offsetX + layout.gameWidth / 2;
    ctx.save();
    ctx.strokeStyle = 'rgba(255,255,255,0.6)';
    ctx.lineWidth = Math.max(2, layout.gameWidth * 0.0015);
    ctx.shadowBlur = 14;
    ctx.shadowColor = 'rgba(255,220,100,0.55)';
    ctx.setLineDash([10, 12]);
    ctx.beginPath();
    ctx.moveTo(midX, layout.offsetY + Math.max(8, layout.gameHeight * 0.02));
    ctx.lineTo(midX, layout.offsetY + layout.gameHeight - Math.max(8, layout.gameHeight * 0.02));
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();
  }

  private drawTargetDetectionProgressScan(layout: RemoteViewLayout, progress: number | null): void {
    const ctx = this.ctx;
    const p =
      progress === null || progress === undefined ? 0 : Math.max(0, Math.min(1, progress));
    if (p <= 0) return;

    const { boxX, boxY, boxW, boxH, borderW } = getRemoteCalibStandZoneMetrics(layout, 'full');
    const inset = Math.max(4, borderW * 1.2);
    const innerW = boxW - 2 * inset;
    const innerH = boxH - 2 * inset;
    const innerX = boxX + inset;
    const innerY = boxY + inset;
    const fillH = innerH * p;
    const fillY = innerY + innerH - fillH;

    ctx.save();
    const g = ctx.createLinearGradient(innerX, fillY + fillH, innerX, fillY);
    g.addColorStop(0, 'rgba(25, 90, 200, 0.55)');
    g.addColorStop(0.55, 'rgba(55, 140, 255, 0.42)');
    g.addColorStop(1, 'rgba(120, 190, 255, 0.3)');
    ctx.fillStyle = g;
    ctx.fillRect(innerX, fillY, innerW, fillH);
    ctx.restore();
  }

  private drawTargetCalibInstructions(layout: RemoteViewLayout): void {
    const ctx = this.ctx;
    const { gameHeight } = layout;
    const { cx, boxX, boxY, boxW, borderW } = getRemoteCalibStandZoneMetrics(layout, 'full');

    // Larger, more readable font — use boxW / 14 so it scales up in 2-player split view
    const instrPx = Math.round(Math.min(gameHeight * 0.034, boxW / 14, 46));
    const instrPadX = boxW * 0.05;
    const sw = boxW - instrPadX * 2;
    const lineGap = instrPx * 1.45;

    const line1 = translate('calib.hint.full_body');
    const line2 = translate('calib.hint.touch_zone');

    ctx.save();
    // Must set font before measuring for wrap
    ctx.font = `600 ${instrPx}px Arial, sans-serif`;

    const wrapped1 = wrapCanvasText(ctx, line1, sw);
    const wrapped2 = wrapCanvasText(ctx, line2, sw);
    const allLines = [...wrapped1, ...wrapped2];

    const totalLines = allLines.length;
    const stripPadV = instrPx * 0.55;
    const stripH = totalLines * lineGap + stripPadV * 2;
    const insetTop = borderW * 2.2 + Math.max(10, gameHeight * 0.015);

    const sx = boxX + instrPadX;
    const sy = boxY + insetTop;
    const stripR = Math.min(14, boxW * 0.04);

    // Background pill
    ctx.fillStyle = 'rgba(0,0,0,0.58)';
    ctx.beginPath();
    if (typeof ctx.roundRect === 'function') {
      ctx.roundRect(sx, sy, sw, stripH, stripR);
    } else {
      ctx.rect(sx, sy, sw, stripH);
    }
    ctx.fill();

    // Text
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowBlur = 14;
    ctx.shadowColor = 'rgba(0,0,0,0.92)';
    ctx.fillStyle = '#ffe566';

    const firstLineY = sy + stripPadV + lineGap * 0.5;
    allLines.forEach((line, i) => {
      ctx.fillText(line, cx, firstLineY + i * lineGap);
    });
    ctx.restore();
  }

  /** Плавающие подписи из натива: `+1`, `-2`, «Уровень N · %» и т.д. (`labels` в JSON). */
  private drawTargetLabels(state: TargetDisplayState, layout: RemoteViewLayout): void {
    const ctx = this.ctx;
    const { offsetX, offsetY, gameWidth, gameHeight } = layout;
    if (state.labels.length === 0) return;

    const fontPx = Math.max(16, Math.round(gameHeight * 0.048));
    const lineW = Math.max(2, fontPx * 0.11);
    ctx.font = `900 ${fontPx}px 'Arial Black', Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineWidth = lineW;
    ctx.lineJoin = 'round';
    ctx.strokeStyle = 'rgba(0,0,0,0.82)';

    for (const lb of state.labels) {
      const px = offsetX + lb.nx * gameWidth;
      const py = offsetY + lb.ny * gameHeight;
      const a = Math.max(0, Math.min(1, lb.alpha));
      if (a < 0.03) continue;

      ctx.globalAlpha = a;
      ctx.fillStyle = lb.color;
      ctx.strokeText(lb.text, px, py);
      ctx.fillText(lb.text, px, py);
    }
    ctx.globalAlpha = 1;
  }

  private drawTargetCircles(state: TargetDisplayState, layout: RemoteViewLayout): void {
    const ctx = this.ctx;
    const { offsetX, offsetY, gameWidth, gameHeight } = layout;
    const gh = gameHeight;

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    let lastNumPx = -1;

    for (const t of state.targets) {
      const px = offsetX + t.nx * gameWidth;
      const py = offsetY + t.ny * gh;
      const r = t.radius * gh;
      const alpha = 1 - t.hitAnimationProgress;
      if (alpha < 0.01) continue;
      ctx.globalAlpha = alpha;

      ctx.beginPath();
      ctx.arc(px, py, r, 0, Math.PI * 2);
      ctx.fillStyle = t.fillColor;
      ctx.fill();

      const lw = Math.max(2, r * 0.08);
      ctx.lineWidth = lw;
      ctx.strokeStyle = 'rgba(255,255,255,0.95)';
      ctx.stroke();

      const remain = Math.max(0, 1 - t.lifetimeProgress);
      ctx.beginPath();
      ctx.arc(px, py, r + lw * 0.4, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * remain);
      ctx.strokeStyle = 'rgba(255,255,255,0.75)';
      ctx.lineWidth = Math.max(3, r * 0.1);
      ctx.stroke();

      if (t.showArcadeNumber && t.arcadeSequenceNumber > 0) {
        const numPx = Math.round(r * 1.1);
        if (numPx !== lastNumPx) {
          ctx.font = `900 ${numPx}px 'Arial Black', Arial, sans-serif`;
          lastNumPx = numPx;
        }
        ctx.fillStyle = 'rgba(0,0,0,0.55)';
        ctx.fillText(String(t.arcadeSequenceNumber), px + 1, py + 1);
        ctx.fillStyle = '#ffffff';
        ctx.fillText(String(t.arcadeSequenceNumber), px, py);
      }
    }
    ctx.globalAlpha = 1;
  }

  private drawTargetAttackBackground(W: number, H: number): void {
    const ctx = this.ctx;
    const img = this.bgImage;
    if (img.complete && img.naturalWidth > 0) {
      drawImageCover(ctx, img, 0, 0, W, H);
      return;
    }
    const cacheKey = `${W}x${H}`;
    if (!this.bgGradientCache || this.bgGradientCacheKey !== cacheKey) {
      const g = ctx.createLinearGradient(0, 0, W, H);
      g.addColorStop(0, '#050816');
      g.addColorStop(0.45, '#0a1530');
      g.addColorStop(1, '#120828');
      this.bgGradientCache = g;
      this.bgGradientCacheKey = cacheKey;
    }
    ctx.fillStyle = this.bgGradientCache;
    ctx.fillRect(0, 0, W, H);
    ctx.save();
    ctx.globalAlpha = 0.22;
    const g2 = ctx.createRadialGradient(W * 0.5, H * 0.35, 0, W * 0.5, H * 0.35, Math.max(W, H) * 0.55);
    g2.addColorStop(0, 'rgba(80, 140, 255, 0.5)');
    g2.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = g2;
    ctx.fillRect(0, 0, W, H);
    ctx.restore();
  }
}
